import { Link } from '@tanstack/react-router';
import { Popover } from '@bb/ui';
import type { CalendarItem, CalendarOverlap } from '../../api/client.ts';
import { CHANNEL_LABELS } from '../campaigns/labels.ts';
import { dayLabel } from './dates.ts';

/** The campaign or Canvas page of an item. */
export function ObjectLink({ environmentId, item }: { environmentId: string; item: Pick<CalendarItem, 'objectType' | 'objectId' | 'name'> }) {
  return item.objectType === 'campaign' ? (
    <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId: item.objectId }}>
      {item.name}
    </Link>
  ) : (
    <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId: item.objectId }}>
      {item.name}
    </Link>
  );
}

const channelsText = (channels: CalendarItem['channels']) => channels.map((c) => CHANNEL_LABELS[c]).join(', ') || 'No channels';

/** A scheduled run in a day cell: time, object link, type and channels. */
export function RunEntry({ environmentId, item }: { environmentId: string; item: CalendarItem }) {
  const paused = item.status.startsWith('paused');
  return (
    <li className={`calendar__run calendar__run--${item.objectType}${paused ? ' calendar__run--paused' : ''}`}>
      <span className="calendar__time">{item.time}</span>
      {item.localTime && <abbr title="In each user's local time">local</abbr>} <ObjectLink environmentId={environmentId} item={item} />
      <span className="calendar__meta">
        {item.objectType === 'canvas' ? 'Canvas' : 'Campaign'} · {channelsText(item.channels)}
        {paused ? ' · Paused' : ''}
      </span>
    </li>
  );
}

/** «N overlaps» on a day: the pairs of runs that share a channel, in a popover. */
export function OverlapBadge({ environmentId, day, overlaps, items }: { environmentId: string; day: string; overlaps: CalendarOverlap[]; items: Map<string, CalendarItem> }) {
  if (!overlaps.length) return null;
  const label = `${overlaps.length} ${overlaps.length === 1 ? 'overlap' : 'overlaps'}`;
  return (
    <Popover
      label={`Overlaps on ${dayLabel(day)}`}
      trigger={
        <button type="button" className="calendar__overlap" aria-label={`${label} on ${dayLabel(day)}`}>
          {label}
        </button>
      }
    >
      <p className="hint">Sends of different campaigns and Canvases on the same day through the same channel reach the same users.</p>
      <ul className="calendar__overlaps">
        {overlaps.map((o) => {
          const a = items.get(o.a);
          const b = items.get(o.b);
          return (
            <li key={`${o.a}|${o.b}`}>
              {a && <ObjectLink environmentId={environmentId} item={a} />} {a?.time} + {b && <ObjectLink environmentId={environmentId} item={b} />} {b?.time} · {channelsText(o.channels)}
            </li>
          );
        })}
      </ul>
    </Popover>
  );
}

/** Action-based and API-triggered delivery: the period it can send in. */
export function windowText(item: CalendarItem): string {
  const span = item.startDay === item.endDay ? dayLabel(item.startDay) : `${dayLabel(item.startDay)} – ${dayLabel(item.endDay)}`;
  return `${span} · ${item.deliveryType === 'api_triggered' ? 'API-triggered' : 'Action-based'} · ${channelsText(item.channels)}${item.end ? '' : ' · no end date'}`;
}
